import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  FiberManualRecord as BulletIcon,
} from '@mui/icons-material';
import MarkdownRenderer from './MarkdownRenderer';
import { linkifyCrossRefs } from './CrossRefLink';

/**
 * Renders a section's structured details from the `details` object.
 * Falls back to MarkdownRenderer if no details available.
 * Cross-reference codes (F001, US001, PF001) are rendered as clickable chips.
 * Reusable across ALL task split views.
 */

// Fields to skip (already shown in content or redundant)
const SKIP_FIELDS = ['description'];

// Fields to render as bullet lists
const LIST_FIELDS = new Set([
  'acceptance_criteria', 'user_journey', 'out_of_scope', 'assumptions',
  'ui_ux_considerations', 'technical_notes', 'key_capabilities', 'rationale',
  'alternatives_considered', 'preconditions', 'postconditions', 'normal_flow',
  'alternative_flows', 'success_metrics',
]);

const toLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const SectionDetailsRenderer = ({ section, projectId }) => {
  const details = section?.details || {};
  const content = section?.content || section?.rawContent || '';
  const hasDetails = Object.keys(details).length > 0;

  if (!hasDetails) {
    return <MarkdownRenderer content={content} />;
  }

  // Separate list fields from text fields
  const entries = Object.entries(details).filter(
    ([key, value]) => !SKIP_FIELDS.includes(key) && value != null && value !== '' && !(Array.isArray(value) && value.length === 0)
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Description from details (not raw content which has markdown syntax) */}
      {details.description && (
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
          {linkifyCrossRefs(details.description, projectId) || details.description}
        </Typography>
      )}

      {/* All detail fields as labeled sections */}
      {entries.map(([key, value]) => {
        const isArray = Array.isArray(value);
        const isList = isArray && LIST_FIELDS.has(key);

        // Array field — render as bullet list
        if (isList || isArray) {
          return (
            <Box key={key} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {toLabel(key)}
              </Typography>
              <List dense disablePadding>
                {value.map((item, idx) => (
                  <ListItem key={idx} disablePadding sx={{ py: 0.15, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 22, mt: 0.8 }}>
                      <BulletIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={linkifyCrossRefs(item, projectId) || item}
                      primaryTypographyProps={{ variant: 'body1', sx: { lineHeight: 1.6 } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        }

        // Text field — short values inline, long values on next line
        const strValue = String(value);
        const isShort = strValue.length < 80;
        return (
          <Box key={key} sx={{ mb: 1.5, display: isShort ? 'flex' : 'block', gap: isShort ? 1 : 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: isShort ? 0 : 0.25, whiteSpace: 'nowrap' }}>
              {toLabel(key)}{isShort ? ':' : ''}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {linkifyCrossRefs(strValue, projectId) || strValue}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default SectionDetailsRenderer;
